import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useVideoCall } from '../../context/VideoCallContext';
import {
  Calendar,
  Clock,
  Stethoscope,
  LogOut,
  Plus,
  Search,
  MessageCircle,
  User,
  X,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
  Lock,
  Timer
} from 'lucide-react';
import doctorService from '../../services/doctorService';
import patientService from '../../services/patientService';
import appointmentService from '../../services/appointmentService';
import medicalRecordService from '../../services/medicalRecordService';
import DocumentDownloadMenu from '../../components/common/DocumentDownloadMenu';
import { isValidDocKind, documentLabel } from '../../utils/documentAvailability';
import paymentService from '../../services/paymentService';
import { FUNNEL, trackFunnelStep, trackPurchase } from '../../services/analytics';
import ChatBox from '../../components/common/ChatBox';
import PresenceNotification from '../../components/common/PresenceNotification';
import PermissionsAlert from '../../components/common/PermissionsAlert';
import PaymentModal from '../../components/patient/PaymentModal';
import RescheduleAppointmentModal from '../../components/patient/RescheduleAppointmentModal';
import WeeklyAvailabilityCalendar from '../../components/patient/WeeklyAvailabilityCalendar';
import SlotPicker from '../../components/common/SlotPicker';
import useAvailableSlots from '../../hooks/useAvailableSlots';
import WhatsappOptInToggle from '../../components/common/WhatsappOptInToggle';
import DocumentEditor from '../../components/common/DocumentEditor';
import BirthDateEditor from '../../components/patient/BirthDateEditor';
import { checkAppointmentAccess, formatAccessMessage } from '../../utils/appointmentAccess';
import {
  APPOINTMENT_TIME_ZONE,
  formatAppointmentDate,
  formatAppointmentTime,
  getTodayInBogota,
  slotToDate,
} from '../../utils/appointmentSchedule';
import { canPatientChangeAppointment } from '../../utils/appointmentChanges';
import { formatDoctorName } from '../../utils/doctorName';
import { debugLog } from '../../utils/logger';
import usePricing from '../../hooks/usePricing';
import { formatCOP } from '../../config/pricing';

const QUOTE_ERROR =
  'No pudimos calcular el valor de la consulta para este horario. Intenta de nuevo en unos segundos o elige otra hora.';

const PATIENT_TABS = [
  { id: 'doctors', label: 'Doctores', Icon: Search },
  { id: 'appointments', label: 'Mis Citas', Icon: Calendar },
  { id: 'medical-records', label: 'Mis Historias', Icon: FileText },
  { id: 'profile', label: 'Perfil', Icon: User },
];

function DashboardPatient() {
  const { user, logout } = useAuth();
  // Documento al que apuntaba el enlace del correo, pendiente de que el
  // paciente pulse el botón (el navegador solo permite abrir la pestaña con un gesto).
  const [pendingDoc, setPendingDoc] = useState(null);
  const [pendingDocError, setPendingDocError] = useState('');
  // Cita a la que apuntaba el SMS de cita sin pagar (?cita=), pendiente de
  // que carguen las citas para abrir su detalle.
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [activeTab, setActiveTab] = useState('doctors');
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  // Seguimiento gratuito (regla de negocio #5): `followUpOffer` guarda lo que
  // responde el backend sobre si el paciente tiene derecho con este médico, y
  // `useFollowUp` si ha decidido usarlo.
  const [followUpOffer, setFollowUpOffer] = useState(null);
  const [useFollowUp, setUseFollowUp] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [quote, setQuote] = useState({ data: null, loading: false, error: '' });
  // Tarifa pública (solo para "Desde $…" en las tarjetas de médicos).
  const { pricing } = usePricing();
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para chat
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // Estados para videollamada (se abre desde dentro del chat)
  // El modal de videollamada lo monta VideoCallProvider (uno solo para toda la
  // app), para que el timbre entrante funcione también desde el chat.
  const { openVideoCall } = useVideoCall();

  // Estados para pago
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Estado para control de acceso
  const [appointmentAccess, setAppointmentAccess] = useState(null);

  // Cancelar / reagendar (solo mientras la cita no esté pagada)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [changeError, setChangeError] = useState('');

  const [appointmentData, setAppointmentData] = useState({
    selectedDate: getTodayInBogota(),
    selectedTime: '',
    reason: '',
    symptoms: '',
  });

  // Perfil clínico del paciente. Sin dateOfBirth se le pide en el modal de
  // agendar: la edad sale en la historia clínica y en las órdenes.
  const [patientProfile, setPatientProfile] = useState(null);
  const [birthDate, setBirthDate] = useState('');
  const needsBirthDate = Boolean(patientProfile) && !patientProfile.dateOfBirth;

  // Aviso cuando la hora elegida en el calendario semanal ya no está libre al
  // abrir el modal (otro paciente la tomó entretanto).
  const [slotNotice, setSlotNotice] = useState('');
  // Se incrementa al cerrar el modal sin reservar o al fallar la reserva, para
  // que el calendario semanal vuelva a pedir la disponibilidad.
  const [calendarRefresh, setCalendarRefresh] = useState(0);

  // ✅ SANITIZADOR CRÍTICO - Convierte objetos anidados en valores seguros para React
  const sanitizeMedicalRecords = (records) => {
    if (!Array.isArray(records)) {
      console.error('❌ Records no es un array:', records);
      return [];
    }

    return records.map((record, index) => {
      try {
        const sanitized = {
          _id: record._id || `temp-${index}`,
          createdAt: record.createdAt || new Date().toISOString(),
          reason: typeof record.reason === 'string' ? record.reason : 'N/A',
          diagnosis: typeof record.diagnosis === 'string' ? record.diagnosis : null,
          // Solo lo que el menú de descarga necesita para saber qué documentos
          // tiene la atención (ver utils/documentAvailability.js). Se reconstruye
          // campo a campo, con la misma desconfianza que el resto del sanitizador:
          // sin esto el menú ofrecería siempre únicamente la historia clínica.
          conduct: {
            medications: Array.isArray(record.conduct?.medications)
              ? record.conduct.medications.map((m) => ({ name: typeof m?.name === 'string' ? m.name : '' }))
              : []
          },
          requestedTests: {
            enabled: record.requestedTests?.enabled === true,
            items: Array.isArray(record.requestedTests?.items)
              ? record.requestedTests.items.map((i) => ({ name: typeof i?.name === 'string' ? i.name : '' }))
              : []
          },
          sickLeave: {
            enabled: record.sickLeave?.enabled === true,
            startDate: typeof record.sickLeave?.startDate === 'string' ? record.sickLeave.startDate : null,
            endDate: typeof record.sickLeave?.endDate === 'string' ? record.sickLeave.endDate : null,
            days: typeof record.sickLeave?.days === 'number' || typeof record.sickLeave?.days === 'string'
              ? record.sickLeave.days
              : null
          },
          doctor: {
            _id: record.doctor?._id || null,
            specialization: record.doctor?.specialization || 'Especialidad no especificada',
            user: {
              _id: record.doctor?.user?._id || null,
              profile: {
                firstName: record.doctor?.user?.profile?.firstName || '',
                lastName: record.doctor?.user?.profile?.lastName || ''
              }
            }
          }
        };

        return sanitized;
      } catch (error) {
        console.error('❌ Error sanitizando record:', error, record);
        return {
          _id: `error-${index}`,
          createdAt: new Date().toISOString(),
          reason: 'Error cargando datos',
          diagnosis: null,
          doctor: {
            _id: null,
            specialization: 'No disponible',
            user: {
              _id: null,
              profile: {
                firstName: '',
                lastName: ''
              }
            }
          }
        };
      }
    });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Los correos de historia clínica enlazan aquí con ?record= y, si la atención
  // trae más de un documento, ?doc=. Antes enlazaban a /medical-records/:id, una
  // ruta que no existe en App.js: el paciente terminaba en la portada.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const recordId = params.get('record');
    const doc = params.get('doc');
    if (!recordId || !doc) return;

    // Se consume el enlace: sin limpiar la URL, recargar la página volvería a
    // abrir el PDF (y el doble montaje de StrictMode lo abriría dos veces).
    params.delete('record');
    params.delete('doc');
    const rest = params.toString();
    window.history.replaceState(
      window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));

    // La lista cerrada evita mandar al backend un doc arbitrario de la URL.
    if (!isValidDocKind(doc)) return;

    // No se abre aquí: window.open tras un await y sin gesto del usuario lo
    // bloquean todos los navegadores. Se deja un aviso con botón (ver más abajo).
    setPendingDoc({ recordId, doc });
  }, []);

  // El SMS de cita sin pagar enlaza aquí con ?cita=. Se abre el detalle de la
  // cita y no el pago directamente: el widget de pago necesita un clic.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appointmentId = params.get('cita');
    if (!appointmentId) return;

    // Igual que con ?record=: se consume para que recargar no la reabra.
    params.delete('cita');
    const rest = params.toString();
    window.history.replaceState(
      window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));

    setPendingAppointmentId(appointmentId);
  }, []);

  useEffect(() => {
    if (!pendingAppointmentId || loading) return;
    // Solo se abre si la cita es de este paciente (está en su lista).
    const appointment = appointments.find((a) => a._id === pendingAppointmentId);
    setPendingAppointmentId(null);
    if (!appointment) return;
    setActiveTab('appointments');
    setSelectedAppointment(appointment);
    setPaymentStatus(null);
  }, [pendingAppointmentId, appointments, loading]);

  const handleOpenPendingDoc = async () => {
    if (!pendingDoc) return;
    setPendingDocError('');
    try {
      await medicalRecordService.generatePDF(pendingDoc.recordId, pendingDoc.doc);
      setPendingDoc(null);
    } catch (err) {
      console.error('No se pudo abrir el documento del enlace del correo:', err);
      // El aviso se queda para poder reintentar.
      setPendingDocError('No se pudo abrir el documento. Inténtalo de nuevo.');
    }
  };

  const closePendingDoc = () => {
    setPendingDoc(null);
    setPendingDocError('');
  };

  // 🔄 Actualizar acceso cada minuto
  // FIX: Se pasa paymentStatus?.status como tercer argumento para que
  // checkAppointmentAccess no intente leerlo del objeto appointment
  // (el campo appointment.payment nunca viene populado desde la API).
  useEffect(() => {
    if (!selectedAppointment) {
      setAppointmentAccess(null);
      return;
    }

    const updateAccess = () => {
      const access = checkAppointmentAccess(
        selectedAppointment,
        'patient',
        paymentStatus?.status ?? null
      );
      debugLog('\n🔐 [useEffect] Acceso actualizado:', access);
      setAppointmentAccess(access);
    };

    updateAccess();

    const interval = setInterval(updateAccess, 60000);
    return () => clearInterval(interval);
    // paymentStatus se incluye en las dependencias para que el acceso
    // se recalcule en cuanto llega la respuesta de getPaymentStatus.
  }, [selectedAppointment, paymentStatus]);

  useEffect(() => {
    const loadPaymentStatus = async () => {
      if (!selectedAppointment?._id) {
        setPaymentStatus(null);
        return;
      }

      try {
        const status = await paymentService.getPaymentStatus(selectedAppointment._id);
        debugLog('💳 Estado de pago cargado:', status);
        setPaymentStatus(status);

        // Conversión de compra para Google Ads. Las citas sin costo (seguimiento
        // gratuito dentro de 7 días) también llegan como 'completed' pero sin
        // transactionId: trackPurchase las descarta para no inflar el valor.
        if (status?.status === 'completed') {
          trackPurchase({
            transactionId: status.transactionId,
            value: status.amount,
          });
        }
      } catch (error) {
        debugLog('⚠️ Pago no encontrado');
        setPaymentStatus(null);
      }
    };

    loadPaymentStatus();
  }, [selectedAppointment?._id]);

  // Slots de 30 min (hora de Bogotá GMT-5) del médico para la fecha elegida
  // en el modal de agendar cita.
  const { slots: availableSlots, loading: loadingSlots, error: slotsError } = useAvailableSlots(
    selectedDoctor?._id,
    appointmentData.selectedDate,
    {
      enabled: showModal,
      onLoaded: (slots, res) => {
        // La hora pudo venir preseleccionada del calendario semanal, y otro
        // paciente pudo tomarla entretanto: se quita para no dejar marcado un
        // botón deshabilitado. Mientras cargan no hay botones, así que la hora
        // seleccionada aquí sigue siendo la que vino del calendario.
        const { selectedTime } = appointmentData;
        if (selectedTime && !slots.some((slot) => slot.time === selectedTime && slot.available)) {
          setSlotNotice('La hora que elegiste ya no está disponible. Elige otra.');
          setAppointmentData((prev) =>
            (prev.selectedTime === selectedTime ? { ...prev, selectedTime: '' } : prev));
        } else {
          setSlotNotice('');
        }

        // Cuántos horarios vio de verdad. Un promedio de 0 aquí significa que
        // el embudo muere por falta de disponibilidad, no por el formulario.
        trackFunnelStep(FUNNEL.VIEW_SLOTS, {
          slots_count: slots.length,
          doctor_available: Boolean(res.available),
        });
      },
    }
  );

  const loadData = async () => {
    try {
      let profileData = null;
      try {
        const profileRes = await patientService.getMyProfile();
        profileData = profileRes.data;
        debugLog('📋 Perfil del paciente cargado:', profileData);
      } catch (error) {
        if (error.response?.status === 404) {
          debugLog('⚠️ Perfil no encontrado, creando...');
          const newProfile = await createDefaultProfile();
          profileData = newProfile;
        } else {
          console.error('❌ Error cargando perfil:', error);
          throw error;
        }
      }

      setPatientProfile(profileData);
      const patientId = profileData?._id;

      if (!patientId) {
        console.error('❌ No se pudo obtener patientId');
        setLoading(false);
        return;
      }

      debugLog('✅ PatientId para cargar historias:', patientId);

      const [doctorsRes, appointmentsRes, medicalRecordsRes] = await Promise.all([
        doctorService.getAllDoctors(),
        appointmentService.getMyAppointments(),
        medicalRecordService.getPatientMedicalRecords(patientId),
      ]);

      setDoctors(doctorsRes.data);
      setAppointments(appointmentsRes.data);

      debugLog('📑 Historias clínicas RAW:', medicalRecordsRes.data);

      const sanitizedRecords = sanitizeMedicalRecords(medicalRecordsRes.data || []);
      debugLog('🧼 Historias clínicas SANITIZADAS:', sanitizedRecords);

      setMedicalRecords(sanitizedRecords);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      setMedicalRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultProfile = async () => {
    try {
      // Sin dateOfBirth: antes se rellenaba con 01/01/1990 y todos los
      // documentos clínicos salían con la misma edad. Se pide al agendar.
      const newProfile = await patientService.createProfile({
        gender: 'Otro',
      });
      debugLog('✅ Perfil creado:', newProfile.data);
      return newProfile.data;
    } catch (error) {
      console.error('❌ Error creating profile:', error);
      throw error;
    }
  };

  // `preset` llega desde el calendario semanal con el hueco ya elegido; desde
  // las tarjetas no viene y el modal abre en hoy sin hora.
  const handleBookAppointment = (doctor, preset = {}) => {
    setSelectedDoctor(doctor);
    setAppointmentData({
      selectedDate: preset.date || getTodayInBogota(),
      selectedTime: preset.time || '',
      reason: '',
      symptoms: '',
    });
    // Se resuelve en el efecto de abajo, al conocer el médico.
    setFollowUpOffer(null);
    setUseFollowUp(false);
    setShowModal(true);
  };

  // Al abrir el modal, preguntar al backend si esta cita puede ser un
  // seguimiento gratuito. Si falla, simplemente no se ofrece: la reserva
  // normal (cobrando) sigue funcionando.
  useEffect(() => {
    if (!showModal || !selectedDoctor?._id) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await appointmentService.getFollowUpEligibility(selectedDoctor._id);
        if (cancelled) return;
        const offer = res?.data ?? null;
        setFollowUpOffer(offer);
        // Se marca por defecto: si tiene derecho, lo normal es quererlo.
        setUseFollowUp(Boolean(offer?.eligible));
      } catch (err) {
        if (cancelled) return;
        debugLog('No se pudo consultar elegibilidad de seguimiento:', err?.message);
        setFollowUpOffer(null);
        setUseFollowUp(false);
      }
    })();

    return () => { cancelled = true; };
  }, [showModal, selectedDoctor]);

  // Cotización del horario elegido: el backend calcula base + recargo nocturno
  // o dominical. El paciente ve el desglose ANTES de confirmar; sin cotización
  // no se puede agendar (no se muestra ningún valor inventado).
  useEffect(() => {
    if (!showModal || !appointmentData.selectedTime) {
      setQuote({ data: null, loading: false, error: '' });
      return undefined;
    }
    const scheduledAt = slotToDate(appointmentData.selectedDate, appointmentData.selectedTime);
    if (Number.isNaN(scheduledAt.getTime())) {
      setQuote({ data: null, loading: false, error: 'La fecha y hora seleccionadas no son válidas' });
      return undefined;
    }

    let cancelled = false;
    setQuote({ data: null, loading: true, error: '' });
    appointmentService.getQuote(scheduledAt.toISOString())
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        if (data && Number.isFinite(data.total)) {
          setQuote({ data, loading: false, error: '' });
        } else {
          setQuote({ data: null, loading: false, error: QUOTE_ERROR });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        debugLog('No se pudo cotizar la consulta:', err?.message);
        setQuote({ data: null, loading: false, error: QUOTE_ERROR });
      });

    return () => { cancelled = true; };
  }, [showModal, appointmentData.selectedDate, appointmentData.selectedTime]);

  const isFreeFollowUp = Boolean(followUpOffer?.eligible && useFollowUp);
  // La cita de seguimiento gratuita no necesita cotización; cualquier otra sí.
  const missingQuote = !isFreeFollowUp && !quote.data;

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!appointmentData.selectedTime) {
      alert('Selecciona un horario disponible');
      return;
    }

    if (missingQuote) {
      setQuote((prev) => ({ ...prev, error: prev.error || QUOTE_ERROR }));
      return;
    }

    setIsSubmitting(true);

    try {
      if (needsBirthDate) {
        const updated = await patientService.updateProfile(patientProfile._id, { dateOfBirth: birthDate });
        setPatientProfile(updated.data);
      }

      const scheduledAt = slotToDate(appointmentData.selectedDate, appointmentData.selectedTime);

      if (Number.isNaN(scheduledAt.getTime())) {
        alert('La fecha y hora seleccionadas no son válidas');
        setIsSubmitting(false);
        return;
      }

      await appointmentService.createAppointment({
        doctorId: selectedDoctor._id,
        scheduledDate: scheduledAt.toISOString(),
        reason: appointmentData.reason,
        symptoms: appointmentData.symptoms
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        // El backend revalida la regla; si no se cumple, cobra normalmente.
        isFollowUp: Boolean(followUpOffer?.eligible && useFollowUp),
      });

      const appointmentsRes = await appointmentService.getMyAppointments();
      setAppointments(appointmentsRes.data);
      setShowModal(false);
      setAppointmentData({ selectedDate: getTodayInBogota(), selectedTime: '', reason: '', symptoms: '' });
      setActiveTab('appointments');
      alert('Cita agendada exitosamente!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error al agendar cita');
      // Lo más probable es que el hueco se haya ocupado: el calendario debe
      // dejar de ofrecerlo.
      setCalendarRefresh((n) => n + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async (recordId, doc = 'historia') => {
    setDownloadingPDF(recordId);
    try {
      await medicalRecordService.generatePDF(recordId, doc);
      // PDF se abre en nueva pestaña via dl_token — no se necesita blob
      // PDF ya se abrió en nueva pestaña via dl_token
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar el PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return badges[status] || badges.scheduled;
  };

  const getStatusText = (status) => {
    const texts = {
      scheduled: 'Agendada',
      confirmed: 'Confirmada',
      'in-progress': 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada',
    };
    return texts[status] || status;
  };

  const handleOpenChat = (appointment) => {
    const access = checkAppointmentAccess(appointment, 'patient', paymentStatus?.status ?? null);

    if (!access.canAccess) {
      alert(formatAccessMessage(access));
      return;
    }

    setSelectedAppointment(appointment);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedAppointment(null);
  };

  const handleOpenPaymentModal = (appointment) => {
    trackFunnelStep(FUNNEL.BEGIN_CHECKOUT);
    setSelectedAppointmentForPayment(appointment);
    setShowPaymentModal(true);
  };

  // ── Cancelar / reagendar ───────────────────────────────────────────────
  // Ambas acciones dejan la cita en un estado que ya no es el que hay en
  // pantalla, así que se recarga la lista y se sale del panel de detalles.
  const handleCancelAppointment = async () => {
    if (!selectedAppointment || cancelling) return;

    const confirmed = window.confirm(
      '¿Seguro que quieres cancelar esta cita? Esta acción no se puede deshacer.'
    );
    if (!confirmed) return;

    setCancelling(true);
    setChangeError('');

    try {
      await appointmentService.cancelAppointment(selectedAppointment._id);
      await loadData();
      setSelectedAppointment(null);
    } catch (error) {
      setChangeError(
        error.response?.data?.message || 'No se pudo cancelar la cita. Intenta de nuevo.'
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleRescheduled = async (updatedAppointment) => {
    setShowRescheduleModal(false);
    setChangeError('');
    await loadData();
    // La cita sigue siendo la misma (mismo _id), solo cambió la hora: se
    // mantiene abierto el panel con los datos nuevos.
    if (updatedAppointment) setSelectedAppointment(updatedAppointment);
  };

  const handleOpenVideoCall = (appointment) => {
    const access = checkAppointmentAccess(appointment, 'patient', paymentStatus?.status ?? null);

    if (!access.canAccess) {
      alert(formatAccessMessage(access));
      return;
    }

    openVideoCall(appointment, appointment.doctor);
  };

  const getDoctorSpecialization = (record) => {
    return record.doctor?.specialization || 'Especialidad no especificada';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Notificaciones de presencia */}
      {selectedAppointment && (
        <PresenceNotification
          appointmentId={selectedAppointment._id}
          currentUserRole="patient"
        />
      )}

      {pendingDoc && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <p className="text-blue-900 flex-1">
              Tienes un documento listo: <strong>{documentLabel(pendingDoc.doc)}</strong>
            </p>
            {pendingDocError && (
              <p role="alert" className="text-sm text-red-700 w-full sm:w-auto">{pendingDocError}</p>
            )}
            <button type="button" onClick={handleOpenPendingDoc}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Abrir documento
            </button>
            <button type="button" onClick={closePendingDoc} aria-label="Cerrar aviso"
              className="px-2 py-1 text-blue-700 hover:text-blue-900">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Stethoscope className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">DocEnLinea</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-700">
              {user?.profile?.firstName} {user?.profile?.lastName}
            </span>
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b overflow-x-auto">
          {PATIENT_TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`px-6 py-3 font-semibold border-b-2 transition whitespace-nowrap ${activeTab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-blue-600'
                }`}
            >
              <Icon className="h-4 w-4 inline mr-2" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content - Perfil */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Mi Perfil</h2>
            <DocumentEditor />
            <BirthDateEditor onSaved={setPatientProfile} />
            <WhatsappOptInToggle />
          </div>
        )}

        {/* Tab Content - Doctors */}
        {activeTab === 'doctors' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Gestiona tus consultas médicas en línea
            </h2>

            {doctors.length > 0 && (
              <WeeklyAvailabilityCalendar
                doctors={doctors}
                refreshKey={calendarRefresh}
                onSelectSlot={(doctor, date, time) => handleBookAppointment(doctor, { date, time })}
              />
            )}

            {doctors.length > 0 && (
              <h3 className="text-xl font-bold text-gray-800 pt-2">Nuestros médicos</h3>
            )}

            {doctors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {doctors.map((doctor) => (
                  <div
                    key={doctor._id}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">
                            {formatDoctorName(doctor)}
                          </h3>
                          <p className="text-blue-600 font-semibold">{doctor.specialization}</p>
                        </div>
                        <User className="h-8 w-8 text-gray-400" />
                      </div>

                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          {doctor.yearsOfExperience} años de experiencia
                        </p>
                        {pricing && (
                          <p className="text-lg font-bold text-green-600">
                            Desde {formatCOP(pricing.basePrice)} COP
                          </p>
                        )}
                        {doctor.bio && (
                          <p className="text-sm text-gray-600 line-clamp-3">
                            {doctor.bio}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleBookAppointment(doctor)}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        Agendar Cita
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-600">No hay doctores disponibles</p>
              </div>
            )}
          </div>
        )}

        {/* Tab Content - Appointments */}
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Mis Citas</h2>

            {appointments.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Lista de citas */}
                <div className="lg:col-span-2 space-y-4">
                  {appointments.map((appointment, index) => (
                    <div
                      key={appointment._id || index}
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setPaymentStatus(null);
                      }}
                      className={`bg-white p-6 rounded-lg shadow-md cursor-pointer transition ${selectedAppointment?._id === appointment._id
                        ? 'ring-2 ring-blue-600 shadow-lg'
                        : 'hover:shadow-lg'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">
                            {formatDoctorName(appointment.doctor)}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {appointment.doctor?.specialization}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(appointment.status)
                            }`}
                        >
                          {getStatusText(appointment.status)}
                        </span>
                      </div>

                      <div className="mt-4 space-y-2">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatAppointmentDate(appointment.scheduledDate)}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {formatAppointmentTime(appointment.scheduledDate)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Motivo:</strong> {appointment.reason}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Costo:</strong>{' '}
                          {/* Monto histórico guardado en la cita, no la tarifa actual. */}
                          {Number.isFinite(appointment.payment?.amount)
                            ? (appointment.payment.amount === 0 ? 'Gratis' : `${formatCOP(appointment.payment.amount)} COP`)
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detalles de la cita seleccionada */}
                {selectedAppointment && (
                  <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg mx-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">Detalles de la Cita</h2>
                      <button
                        type="button"
                        onClick={() => setSelectedAppointment(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✗
                      </button>
                    </div>

                    {/* Info de cita */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-sm text-gray-600">Doctor</p>
                        <p className="font-semibold text-gray-800">
                          {formatDoctorName(selectedAppointment.doctor)}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Especialidad</p>
                        <p className="font-semibold text-gray-800">
                          {selectedAppointment.doctor?.specialization}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Fecha y Hora</p>
                        <p className="font-semibold text-gray-800">
                          {formatAppointmentDate(selectedAppointment.scheduledDate)}{' '}
                          {formatAppointmentTime(selectedAppointment.scheduledDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Razón de la cita</p>
                        <p className="font-semibold text-gray-800">{selectedAppointment.reason}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Síntomas</p>
                        <p className="font-semibold text-gray-800">
                          {selectedAppointment.symptoms?.join(', ') || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* 🔐 CONTROL DE ACCESO TEMPORAL */}
                    {appointmentAccess && !appointmentAccess.canAccess && (
                      <div className="mb-6">
                        {appointmentAccess.expired ? (
                          <div className="bg-gray-50 border border-gray-300 p-4 rounded-lg flex items-start gap-3">
                            <Lock className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-gray-800 text-sm">Consulta expirada</p>
                              <p className="text-gray-600 text-xs mt-1">
                                La ventana de acceso ha finalizado
                              </p>
                            </div>
                          </div>
                        ) : appointmentAccess.reason === 'too_early' ? (
                          <div className="bg-yellow-50 border border-yellow-300 p-4 rounded-lg flex items-start gap-3">
                            <Timer className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-yellow-800 text-sm">Consulta próximamente</p>
                              <p className="text-yellow-700 text-xs mt-1">
                                Podrás acceder en {appointmentAccess.remainingTime} minutos
                              </p>
                              <p className="text-yellow-600 text-xs mt-1">
                                Disponible 5 min antes de la hora programada
                              </p>
                            </div>
                          </div>
                        ) : appointmentAccess.reason === 'payment_required' ? (
                          // No mostrar el bloque rojo de "Acceso restringido" si el pago
                          // aún está cargando (paymentStatus === null)
                          paymentStatus !== null && (
                            <div className="bg-red-50 border border-red-300 p-4 rounded-lg flex items-start gap-3">
                              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-semibold text-red-800 text-sm">Acceso restringido</p>
                                <p className="text-red-700 text-xs mt-1">
                                  {formatAccessMessage(appointmentAccess)}
                                </p>
                              </div>
                            </div>
                          )
                        ) : (
                          <div className="bg-red-50 border border-red-300 p-4 rounded-lg flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-red-800 text-sm">Acceso restringido</p>
                              <p className="text-red-700 text-xs mt-1">
                                {formatAccessMessage(appointmentAccess)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* SECCIÓN DE PAGO */}
                    <div className="border-t pt-4 mb-6">
                      {!paymentStatus && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center gap-3">
                          <Loader className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-blue-800 text-sm">Cargando estado...</p>
                            <p className="text-blue-700 text-xs">Por favor espera</p>
                          </div>
                        </div>
                      )}

                      {(!paymentStatus || ['unpaid', 'no_transaction'].includes(paymentStatus?.status)) && (
                        <>
                          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-3 mb-4">
                            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-yellow-800 text-sm">Pago pendiente</p>
                              <p className="text-yellow-700 text-xs">
                                {paymentStatus?.status === 'no_transaction'
                                  ? 'Cerraste el proceso sin completar el pago. Puedes intentarlo nuevamente.'
                                  : 'Realiza el pago para acceder a la consulta'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPaymentModal(selectedAppointment)}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2"
                          >
                            💳 Pagar Ahora
                          </button>
                        </>
                      )}

                      {paymentStatus?.status === 'pending' && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-start gap-3">
                          <Loader className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
                          <div>
                            <p className="font-semibold text-blue-800 text-sm">Procesando pago</p>
                            <p className="text-blue-700 text-xs">Verificando tu pago...</p>
                          </div>
                        </div>
                      )}

                      {paymentStatus?.status === 'completed' && (
                        <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-green-800 text-sm">✅ Pago confirmado</p>
                            <p className="text-green-700 text-xs">
                              {appointmentAccess?.canAccess
                                ? 'Tu consulta está disponible ahora'
                                : 'La consulta estará disponible 5 min antes'}
                            </p>
                          </div>
                        </div>
                      )}

                      {paymentStatus?.status === 'failed' && (
                        <>
                          <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-start gap-3 mb-4">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-red-800 text-sm">Pago rechazado</p>
                              <p className="text-red-700 text-xs">Intenta nuevamente</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPaymentModal(selectedAppointment)}
                            className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition font-semibold flex items-center justify-center gap-2"
                          >
                            <CreditCard className="h-4 w-4" />
                            🔄 Reintentar pago
                          </button>
                        </>
                      )}
                    </div>

                    {/* Cambios sobre la cita: solo mientras no esté pagada.
                        Después del pago queda fija (no hay reembolsos
                        automáticos) y estos botones desaparecen. */}
                    {canPatientChangeAppointment(selectedAppointment, paymentStatus?.status ?? null) && (
                      <div className="border-t pt-4 mb-6">
                        {changeError && (
                          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                            {changeError}
                          </p>
                        )}
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => { setChangeError(''); setShowRescheduleModal(true); }}
                            disabled={cancelling}
                            className="flex-1 border border-blue-600 text-blue-600 py-2 rounded-lg hover:bg-blue-50 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Calendar className="h-4 w-4" />
                            Reagendar
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelAppointment}
                            disabled={cancelling}
                            className="flex-1 border border-red-600 text-red-600 py-2 rounded-lg hover:bg-red-50 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {cancelling ? (
                              <>
                                <Loader className="h-4 w-4 animate-spin" />
                                Cancelando...
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4" />
                                Cancelar cita
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* BOTÓN DE ACCIÓN Único: Iniciar consulta (chat). Desde dentro del chat se puede pasar a video. */}
                    {paymentStatus?.status === 'completed' && appointmentAccess?.canAccess && (
                      <button
                        type="button"
                        onClick={() => handleOpenChat(selectedAppointment)}
                        className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Iniciar consulta
                      </button>
                    )}

                    {/* Mensaje de tiempo restante */}
                    {appointmentAccess?.canAccess && appointmentAccess.remainingTime && (
                      <div className="mt-4 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                        <p className="text-blue-800 text-xs text-center">
                          ⏱️ Tiempo restante: {appointmentAccess.remainingTime} minutos
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-600">No tienes citas agendadas</p>
              </div>
            )}
          </div>
        )}

        {/* Tab Content - Medical Records */}
        {activeTab === 'medical-records' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Mis Historias Clínicas</h2>

            {medicalRecords.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {medicalRecords.map((record) => {
                  const doctorName = formatDoctorName(record.doctor);
                  const specialization = getDoctorSpecialization(record);

                  return (
                    <div key={record._id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-gray-800 line-clamp-2">
                              {doctorName}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {specialization}
                            </p>
                          </div>
                          <FileText className="h-8 w-8 text-blue-600 flex-shrink-0 ml-2" />
                        </div>

                        <div className="space-y-2 mb-4 text-sm">
                          <div>
                            <p className="text-gray-600">Fecha:</p>
                            <p className="font-semibold text-gray-800">
                              {new Date(record.createdAt).toLocaleDateString('es-CO', { timeZone: APPOINTMENT_TIME_ZONE })}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Motivo:</p>
                            <p className="font-semibold text-gray-800 line-clamp-2">
                              {record.reason}
                            </p>
                          </div>
                          {record.diagnosis && (
                            <div>
                              <p className="text-gray-600">Diagnóstico:</p>
                              <p className="font-semibold text-gray-800 line-clamp-2">
                                {record.diagnosis}
                              </p>
                            </div>
                          )}
                        </div>

                        <DocumentDownloadMenu
                          record={record}
                          busy={downloadingPDF === record._id}
                          onDownload={(doc) => handleDownloadPDF(record._id, doc)}
                          className="relative block w-full"
                          buttonClassName="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <div className="flex flex-col items-center">
                  <div className="bg-blue-100 p-4 rounded-full mb-4">
                    <FileText className="h-10 w-10 text-blue-600" />
                  </div>
                  <p className="text-gray-800 font-bold text-lg mb-2">Aún no tienes historias clínicas</p>
                  <p className="text-gray-600 text-sm mb-6 max-w-md">
                    Las historias clínicas aparecerán aquí después de completar tus consultas con los doctores.
                  </p>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('appointments')}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold inline-block"
                    >
                      Ver mis citas
                    </button>
                    <p className="text-xs text-gray-500">
                      Paso 1: Agenda una cita → Paso 2: Completa la consulta → Paso 3: Descarga tu historia
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Agendar Cita con {formatDoctorName(selectedDoctor)}
            </h3>

            <form onSubmit={handleSubmitAppointment} className="space-y-4">
              {needsBirthDate && (
                <div>
                  <label htmlFor="booking-birth-date" className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha de nacimiento
                  </label>
                  <input
                    id="booking-birth-date"
                    type="date"
                    value={birthDate}
                    max={getTodayInBogota()}
                    min="1900-01-01"
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El médico la necesita para tu historia clínica. Solo te la pedimos una vez.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha (hora Colombia, GMT-5)
                </label>
                <input
                  type="date"
                  value={appointmentData.selectedDate}
                  min={getTodayInBogota()}
                  onChange={(e) =>
                    setAppointmentData({
                      ...appointmentData,
                      selectedDate: e.target.value,
                      selectedTime: '',
                    })
                  }
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Horario disponible (slots de 30 min)
                </label>

                {!loadingSlots && slotNotice && (
                  <p role="alert" className="text-sm text-amber-700 py-2">{slotNotice}</p>
                )}

                <SlotPicker
                  slots={availableSlots}
                  loading={loadingSlots}
                  error={slotsError}
                  selectedTime={appointmentData.selectedTime}
                  onSelect={(time) => setAppointmentData({ ...appointmentData, selectedTime: time })}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Razón de la cita
                </label>
                <input
                  type="text"
                  value={appointmentData.reason}
                  onChange={(e) => setAppointmentData({ ...appointmentData, reason: e.target.value })}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Síntomas (separados por coma)
                </label>
                <textarea
                  value={appointmentData.symptoms}
                  onChange={(e) =>
                    setAppointmentData({ ...appointmentData, symptoms: e.target.value })
                  }
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  rows="3"
                />
              </div>

              {/* Seguimiento gratuito: solo se ofrece si el backend confirma
                  que el paciente tiene derecho con este médico. */}
              {followUpOffer?.eligible && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useFollowUp}
                      onChange={(e) => setUseFollowUp(e.target.checked)}
                      disabled={isSubmitting}
                      className="h-4 w-4 mt-1 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-sm">
                      <span className="font-semibold text-green-800">
                        Usar mi cita de seguimiento gratuita
                      </span>
                      <span className="block text-green-700 mt-1">
                        Tienes derecho a un seguimiento sin costo porque ya tuviste una
                        consulta con este médico
                        {followUpOffer.previousAppointmentDate
                          ? ` el ${new Date(followUpOffer.previousAppointmentDate).toLocaleDateString('es-CO', { timeZone: APPOINTMENT_TIME_ZONE })}`
                          : ''}
                        . Válido hasta {followUpOffer.windowDays} días después.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {/* Resumen de cobro, para que no haya sorpresas al confirmar. El
                  desglose (base + recargo) lo calcula el backend para el horario elegido. */}
              <div className="border-t border-gray-200 pt-3 space-y-1" data-testid="booking-summary">
                {quote.data && !isFreeFollowUp && (
                  <>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Valor de la consulta</span>
                      <span>{formatCOP(quote.data.basePrice)} COP</span>
                    </div>
                    {quote.data.surchargeApplied && (
                      <div className="flex items-center justify-between text-sm text-amber-700">
                        <span>
                          + Recargo {quote.data.reason === 'sunday' ? 'dominical' : 'nocturno'}
                          {pricing?.nightStart && pricing?.nightEnd && quote.data.reason === 'night'
                            ? ` (${pricing.nightStart} a ${pricing.nightEnd})`
                            : ''}
                        </span>
                        <span>{formatCOP(quote.data.surcharge)} COP</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total a pagar</span>
                  {isFreeFollowUp ? (
                    <span className="text-lg font-bold text-green-700">
                      Gratis
                      {quote.data && (
                        <span className="ml-2 text-sm font-normal text-gray-500 line-through">
                          {formatCOP(quote.data.total)} COP
                        </span>
                      )}
                    </span>
                  ) : quote.data ? (
                    <span className="text-lg font-bold text-gray-800">
                      {formatCOP(quote.data.total)} COP
                    </span>
                  ) : quote.loading ? (
                    <span className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      Calculando...
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {appointmentData.selectedTime ? '—' : 'Elige un horario para ver el valor'}
                    </span>
                  )}
                </div>
                {!isFreeFollowUp && quote.error && (
                  <p role="alert" className="text-sm text-red-600">{quote.error}</p>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !appointmentData.selectedTime || missingQuote}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Agendando...
                    </>
                  ) : (
                    'Agendar'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setCalendarRefresh((n) => n + 1);
                  }}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-lg hover:bg-gray-400 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showRescheduleModal && selectedAppointment && (
        <RescheduleAppointmentModal
          appointment={selectedAppointment}
          onClose={() => setShowRescheduleModal(false)}
          onRescheduled={handleRescheduled}
        />
      )}

      {showPaymentModal && selectedAppointmentForPayment && (
        <PaymentModal
          appointment={selectedAppointmentForPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedAppointmentForPayment(null);
            loadData();
          }}
          onSuccess={() => {
            loadData();
            setPaymentStatus(null);
            setSelectedAppointment(null);
          }}
        />
      )}

      {/* Chat Box (incluye el boton para pasar a videollamada) */}
      {showChat && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-end p-2 border-b">
              <button
                type="button"
                onClick={handleCloseChat}
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 p-2 rounded-full transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <ChatBox
                appointment={selectedAppointment}
                otherUser={selectedAppointment.doctor}
                onClose={handleCloseChat}
                onOpenVideoCall={() => handleOpenVideoCall(selectedAppointment)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Permissions Alert */}
      <PermissionsAlert />
    </div>
  );
}

export default DashboardPatient;
